export type GEXChapter = 'regime' | 'levels' | 'expirations' | 'confirmation' | 'trade';

export type GEXTone = 'positive' | 'negative' | 'accent' | 'neutral' | 'warning';

/** The glyph in front of one half of a two-part play. */
export type GEXActionIcon = 'fade' | 'up' | 'down' | 'across' | 'column';

export interface GEXFact {
  label: string;
  line: string;
  tone?: GEXTone;
}

/**
 * One explanation card. The claim is the card's only line of running prose; everything under it
 * is a fact in a form the eye can scan: one fact line or up to three bullets, labeled rows,
 * side-by-side cells, or a row of chips. The action is one line, or two glyph lines when the play
 * depends on the reaction at a level. Every card explains the page as it reads; none renames it.
 * The copy guard in gexCopyGuard.test.ts holds the copy rules, and scripts/gex-help-shots.js
 * holds every block to one line on the live page.
 */
export interface GEXLesson {
  title: string;
  /** The first line under the title: what the trader gets. */
  claim: string;
  /** One fact line, or two or three one-line bullets. */
  facts?: string[];
  /** Labeled one-line rows: a legend or a short definition list. */
  rows?: GEXFact[];
  /** Either-or facts side by side. */
  cells?: GEXFact[];
  /** A labeled row of short parts. */
  chips?: { label: string; items: string[] };
  /** One line, or two glyph lines when the play depends on the reaction. */
  action: string | { icon: GEXActionIcon; text: string }[];
  chapter: GEXChapter;
}

/** One definition follows a number through metrics, charts, tables, and the guide. */
export const gexLessons = {
  // ---- The metrics strip ----
  spot: {
    title: 'Spot Price',
    claim: 'Every level is measured from here.',
    facts: ['The live price of the underlying'],
    action: 'Trade the nearest level first.',
    chapter: 'levels',
  },
  net: {
    title: 'Net GEX',
    claim: 'The force under the tape.',
    facts: ['Calls and puts netted, per 1% move'],
    action: 'Price and flow at the level pick the side.',
    chapter: 'regime',
  },
  ratio: {
    title: 'Ratio',
    claim: 'Which side of the board is heavier.',
    facts: ['Call gamma over put gamma'],
    cells: [
      { label: 'Above 1', line: 'Calls dominate', tone: 'positive' },
      { label: 'Below 1', line: 'Puts dominate', tone: 'negative' },
    ],
    action: 'Heavy calls cap rallies; heavy puts cushion.',
    chapter: 'regime',
  },
  call: {
    title: 'Call GEX',
    claim: 'The call side of the board.',
    facts: ['Call gamma in your expirations, per 1% move'],
    action: 'Rallies stall at the heaviest call strikes.',
    chapter: 'regime',
  },
  put: {
    title: 'Put GEX',
    claim: 'The put side of the board.',
    facts: ['Put gamma in your expirations, per 1% move'],
    action: 'Selloffs catch at the heaviest put strikes.',
    chapter: 'regime',
  },
  total: {
    title: 'Total GEX',
    claim: 'How much gamma is in the game.',
    facts: ['Every option’s gamma before netting'],
    action: 'Big Total, small Net: two sides colliding.',
    chapter: 'regime',
  },
  callOI: {
    title: 'Call open interest',
    claim: 'The open calls behind the gamma.',
    facts: ['Call contracts open in your expirations'],
    action: 'A big count near price is a crowded strike.',
    chapter: 'trade',
  },
  putOI: {
    title: 'Put open interest',
    claim: 'The open puts behind the gamma.',
    facts: ['Put contracts open in your expirations'],
    action: 'A big count under price is a crowded floor.',
    chapter: 'trade',
  },
  totalOI: {
    title: 'Total open interest',
    claim: 'Every open contract in view.',
    facts: ['Calls plus puts in your expirations'],
    action: 'Size at your level is conviction there.',
    chapter: 'trade',
  },
  callWall: {
    title: 'Call Wall',
    claim: 'The ceiling the tape has to beat.',
    facts: ['Heaviest positive gamma above price'],
    action: [
      { icon: 'fade', text: 'Rejection: fade it' },
      { icon: 'up', text: 'Break with call flow: ride it' },
    ],
    chapter: 'levels',
  },
  putWall: {
    title: 'Put Wall',
    claim: 'The floor the tape has to break.',
    facts: ['Heaviest negative gamma below price'],
    action: [
      { icon: 'fade', text: 'Hold or reclaim: buy the bounce' },
      { icon: 'down', text: 'Break with put flow: ride it down' },
    ],
    chapter: 'levels',
  },
  zero: {
    title: 'Zero Gamma',
    claim: 'Where the tape changes character.',
    facts: ['Net gamma flips sign at this price'],
    action: [
      { icon: 'fade', text: 'Positive side: fade the extremes' },
      { icon: 'up', text: 'Negative side: ride the break' },
    ],
    chapter: 'levels',
  },
  distance: {
    title: 'Distance from spot',
    claim: 'The move to get there.',
    facts: ['From the live price, in percent', 'Plus is above, minus is below'],
    action: 'The closest level is the first test.',
    chapter: 'levels',
  },

  // ---- Expirations ----
  concentration: {
    title: 'Expiration concentration',
    claim: 'How much of the gamma expires soon.',
    facts: ['Today counts in all three'],
    action: 'Big 0DTE share: trade today’s strikes.',
    chapter: 'expirations',
  },
  expirations: {
    title: 'Expiration selection',
    claim: 'Build the map for your hold.',
    facts: ['Every number rebuilds for your window'],
    cells: [
      { label: 'Day trade', line: 'Today' },
      { label: 'Swing', line: '1W or 1M' },
      { label: 'Position', line: '3M and out' },
    ],
    action: 'Pick the window that matches your hold.',
    chapter: 'expirations',
  },
  expiryShare: {
    title: 'Net gamma and share',
    claim: 'The dates carrying the size.',
    rows: [
      { label: 'Number', line: 'The date’s net gamma' },
      { label: 'Bar', line: 'Its share of the chain’s gamma' },
      { label: '◆', line: 'The date with the most gamma' },
    ],
    action: 'Select the date: its strikes are the levels.',
    chapter: 'expirations',
  },

  // ---- Views and controls ----
  netMode: {
    title: 'Net GEX view',
    claim: 'One bar per strike.',
    facts: ['Calls and puts netted at every strike'],
    action: 'The tallest bars near price are levels.',
    chapter: 'regime',
  },
  splitMode: {
    title: 'Call/Put view',
    claim: 'Both sides, drawn apart.',
    facts: ['Calls up, puts down, at every strike'],
    action: 'A short net bar hides the fight behind it.',
    chapter: 'regime',
  },
  displayModes: {
    title: 'Net GEX and Call/Put',
    claim: 'Two ways to read the board.',
    action: 'Net for the levels, Call/Put for the fight.',
    chapter: 'regime',
  },
  graphMode: {
    title: 'Graph view',
    claim: 'The shape of the board.',
    facts: ['Where the bars stack, price reacts'],
    action: 'Find the tallest bars near price.',
    chapter: 'levels',
  },
  tableMode: {
    title: 'Table view',
    claim: 'Every strike to the dollar.',
    facts: ['GEX and open interest, sortable'],
    action: 'Sort by Net GEX: the top rows are levels.',
    chapter: 'levels',
  },
  viewModes: {
    title: 'Graph and Table',
    claim: 'The shape or the exact numbers.',
    action: 'Spot levels in Graph, size them in Table.',
    chapter: 'levels',
  },
  near: {
    title: 'Near strikes',
    claim: 'The strikes in play.',
    facts: ['The range near price, walls included'],
    action: 'Trade what is in range.',
    chapter: 'levels',
  },
  allStrikes: {
    title: 'All strikes',
    claim: 'The whole board.',
    facts: ['Every strike in your expirations'],
    action: 'The next concentration is the target.',
    chapter: 'levels',
  },
  strikeRange: {
    title: 'Near and All strikes',
    claim: 'Zoom to the fight or see the board.',
    action: 'Open All to find the target after a break.',
    chapter: 'levels',
  },
  simulation: {
    title: 'IV simulation',
    claim: 'Stress-test the map.',
    chips: { label: 'Recalculates', items: ['Exposure', 'Walls', 'Zero Gamma'] },
    facts: ['+10% on 20% IV is 22%'],
    action: 'See which levels survive a vol spike.',
    chapter: 'trade',
  },
  marketIV: {
    title: 'Market IV',
    claim: 'The chain’s real volatility pricing.',
    facts: ['The IV the market quotes right now', 'Your simulation moves the gamma map only'],
    action: 'Price your contract against ATM.',
    chapter: 'trade',
  },

  // ---- Charts and tables ----
  strikeProfile: {
    title: 'Strike Profile',
    claim: 'Where the gamma stacks.',
    facts: [
      'One bar per strike, per 1% move',
      'Green up: positive gamma. Red down: negative',
      'The dashed line is spot',
    ],
    action: 'The tallest bars near spot are the levels.',
    chapter: 'levels',
  },
  putBars: {
    title: 'Put Γ',
    claim: 'Where the gamma runs negative.',
    rows: [
      { label: 'Net GEX', line: 'Strikes where net gamma is negative' },
      { label: 'Call/Put', line: 'Every strike’s put gamma' },
    ],
    action: 'The longest red bar below price is the floor.',
    chapter: 'levels',
  },
  callBars: {
    title: 'Call Γ',
    claim: 'Where the gamma runs positive.',
    rows: [
      { label: 'Net GEX', line: 'Strikes where net gamma is positive' },
      { label: 'Call/Put', line: 'Every strike’s call gamma' },
    ],
    action: 'The longest green bar above is the ceiling.',
    chapter: 'levels',
  },
  heatmap: {
    title: 'GEX Heatmap',
    claim: 'Which date built each level.',
    facts: [
      'Rows: strikes. Columns: expirations',
      'Green positive, red negative',
      'The brighter the cell, the more gamma',
    ],
    action: [
      { icon: 'across', text: 'Across a row: the dates holding a level' },
      { icon: 'column', text: 'Down a column: what a date defends' },
    ],
    chapter: 'expirations',
  },
  heatmapScale: {
    title: 'Put-heavy to Call-heavy',
    claim: 'The brightest cells are the biggest bets.',
    cells: [
      { label: 'Put-heavy', line: 'Negative net gamma', tone: 'negative' },
      { label: 'Call-heavy', line: 'Positive net gamma', tone: 'positive' },
    ],
    facts: ['Brightness is size'],
    action: 'The brightest cell near price is next.',
    chapter: 'expirations',
  },
  expiryChart: {
    title: 'Gamma Exposure by Expiry',
    claim: 'Which dates carry the gamma.',
    facts: ['Every strike rolled into one bar per date', 'Call/Put view splits each date’s sides'],
    action: 'The tallest date is the one to select.',
    chapter: 'expirations',
  },
  priceProfile: {
    title: 'Gamma Price Profile',
    claim: 'The regime at every price.',
    facts: ['Net gamma recomputed across prices', 'The zero crossing is Zero Gamma'],
    cells: [
      { label: 'Above zero', line: 'Ranges hold', tone: 'positive' },
      { label: 'Below zero', line: 'Breaks run', tone: 'negative' },
    ],
    action: 'Walk your price path along the curve.',
    chapter: 'levels',
  },

  // ---- Intraday ΔGEX ----
  delta: {
    title: 'Intraday ΔGEX',
    claim: 'Catch the regime turning.',
    facts: ['Net GEX now against earlier readings'],
    cells: [
      { label: 'Rising', line: 'Toward range', tone: 'positive' },
      { label: 'Falling', line: 'Toward continuation', tone: 'negative' },
    ],
    action: 'A sign flip in session is the day’s tell.',
    chapter: 'confirmation',
  },
  firstSnapshot: {
    title: 'Δ Session',
    claim: 'The move since the timeline began.',
    facts: ['Net GEX now minus the first reading kept'],
    action: 'A board losing positive gamma loosens.',
    chapter: 'confirmation',
  },
  previousSnapshot: {
    title: 'Δ Recent',
    claim: 'The latest shift.',
    facts: ['Net GEX now minus the previous reading'],
    action: 'Gamma momentum confirms price momentum.',
    chapter: 'confirmation',
  },
  snapshotTime: {
    title: 'Snapshot time',
    claim: 'When this reading was taken.',
    facts: ['Your local time'],
    action: 'Match a Net GEX jump to price then.',
    chapter: 'confirmation',
  },

  // ---- Signals ----
  signals: {
    title: 'Signals',
    claim: 'The levels in play right now.',
    rows: [
      { label: 'Volatility', line: 'The regime, and the trapdoor below' },
      { label: 'Magnet', line: 'The heaviest strike near price' },
      { label: 'Support', line: 'A floor below price' },
      { label: 'Resistance', line: 'A ceiling above price' },
      { label: 'STRONG', line: 'Heavy gamma backs the level', tone: 'negative' },
      { label: 'MODERATE', line: 'Lighter gamma behind it', tone: 'warning' },
    ],
    action: 'Trade the reaction at the level.',
    chapter: 'confirmation',
  },
  regimeSignal: {
    title: 'Volatility',
    claim: 'The regime: moves absorbed or amplified.',
    cells: [
      { label: 'Long gamma', line: 'Moves dampen', tone: 'positive' },
      { label: 'Short gamma', line: 'Moves amplify', tone: 'negative' },
    ],
    action: [
      { icon: 'fade', text: 'Long gamma: fade the extremes' },
      { icon: 'up', text: 'Short gamma: ride the break' },
    ],
    chapter: 'regime',
  },
  triggerSignal: {
    title: 'Volatility',
    claim: 'The trapdoor under price.',
    facts: ['The Put Wall with heavy negative gamma'],
    action: [
      { icon: 'down', text: 'Break with put flow: ride it down' },
      { icon: 'fade', text: 'Hold: that is the bounce' },
    ],
    chapter: 'levels',
  },
  magnetSignal: {
    title: 'Magnet',
    claim: 'The heaviest strike near price.',
    facts: ['Peak net gamma, within 2% of price'],
    cells: [
      { label: 'Positive gamma', line: 'Price gravitates to it', tone: 'positive' },
      { label: 'Negative gamma', line: 'Price runs through it', tone: 'negative' },
    ],
    action: 'The reaction there is the tell.',
    chapter: 'levels',
  },
  zeroSignal: {
    title: 'Zero Gamma',
    claim: 'Where the tape changes character.',
    facts: ['Zero Gamma, within 5% of price'],
    cells: [
      { label: 'Positive side', line: 'Ranges hold', tone: 'positive' },
      { label: 'Negative side', line: 'Breaks run', tone: 'negative' },
    ],
    action: 'Price through it: switch playbooks.',
    chapter: 'levels',
  },
  callSignal: {
    title: 'Resistance',
    claim: 'Heavy call gamma overhead.',
    facts: ['Within 10% above price'],
    action: [
      { icon: 'fade', text: 'Rejection: fade it' },
      { icon: 'up', text: 'Break with call flow: ride it' },
    ],
    chapter: 'levels',
  },
  putSignal: {
    title: 'Support',
    claim: 'Heavy put gamma underneath.',
    facts: ['Within 10% below price'],
    action: [
      { icon: 'fade', text: 'Hold: buy it' },
      { icon: 'down', text: 'Break with put flow: ride it down' },
    ],
    chapter: 'levels',
  },

  // ---- Gamma Squeeze Screener ----
  squeeze: {
    title: 'Gamma Squeeze Screener',
    claim: 'The direction with the fuel.',
    facts: ['Both directions scored, 0 to 100'],
    chips: { label: 'Scored on', items: ['Gamma', 'Wall', 'Flow', 'Volume', 'Delta OI'] },
    action: 'Trade the stronger side at its wall.',
    chapter: 'confirmation',
  },
  squeezeBias: {
    title: 'Bias',
    claim: 'The side the board favors.',
    facts: ['One side leads by more than 10 points'],
    action: 'Trade the wall on that side.',
    chapter: 'confirmation',
  },
  squeezeLabel: {
    title: 'Setup label',
    claim: 'Where the score sits.',
    action: 'Imminent: trade the wall on that side.',
    chapter: 'confirmation',
  },
  probabilityScore: {
    title: 'Probability Score',
    claim: 'How much of the squeeze is in place.',
    facts: ['Five conditions, one score out of 100', 'Ticks at 30, 50, and 75 split the labels'],
    action: 'The higher it climbs, the more fuel is in.',
    chapter: 'confirmation',
  },
  gammaFactor: {
    title: 'Gamma Regime',
    claim: 'The fuel.',
    facts: ['Negative gamma fills this bar'],
    action: 'Full bar: breaks run. Empty: walls hold.',
    chapter: 'confirmation',
  },
  wallFactor: {
    title: 'Wall Proximity',
    claim: 'How close the target is.',
    facts: ['The nearer this side’s wall, the fuller'],
    action: 'Full bar: the wall is the next test.',
    chapter: 'confirmation',
  },
  flowFactor: {
    title: 'Flow Alignment',
    claim: 'The money’s vote on this direction.',
    facts: ['Full when flow pays up this way', 'Dashed until the flow read arrives'],
    action: 'Trade the squeeze with flow behind it.',
    chapter: 'confirmation',
  },
  volumeFactor: {
    title: 'Volume Confirm',
    claim: 'The pace of the tape.',
    facts: ['Full above twice the recent pace', 'Dashed until 20 visible trades print'],
    action: 'Acceleration into a wall starts the run.',
    chapter: 'confirmation',
  },
  deltaOIFactor: {
    title: 'Delta OI Alignment',
    claim: 'Which side the open positions lean.',
    facts: ['Open interest weighted by delta'],
    cells: [
      { label: 'Call-heavy', line: 'Backs the bullish side', tone: 'positive' },
      { label: 'Put-heavy', line: 'Backs the bearish side', tone: 'negative' },
    ],
    action: 'The lean shows which squeeze has the crowd.',
    chapter: 'confirmation',
  },
  keyLevels: {
    title: 'Key Levels',
    claim: 'The prices this squeeze runs between.',
    rows: [
      { label: 'Current Price', line: 'Where the tape trades now' },
      { label: 'Wall', line: 'The target the squeeze runs to' },
      { label: 'Trigger Level', line: 'The price that sets it off' },
    ],
    action: 'Through the trigger, toward the wall.',
    chapter: 'confirmation',
  },
  triggerLevel: {
    title: 'Trigger Level',
    claim: 'The price that sets the squeeze off.',
    rows: [
      { label: 'Bullish', line: 'Zero Gamma' },
      { label: 'Bearish', line: 'The Put Wall, heavy negative' },
    ],
    action: 'A move through it is the trigger.',
    chapter: 'confirmation',
  },
  setupAnalysis: {
    title: 'Setup Analysis',
    claim: 'Four conditions, checked.',
    chips: { label: 'Checks', items: ['Gamma', 'Wall', 'Flow', 'Volume'] },
    rows: [
      { label: 'In place', line: 'This condition is loaded' },
      { label: 'Building', line: 'Some of it is in place' },
      { label: 'Missing', line: 'This one still has to land' },
    ],
    action: 'Every check in place loads the squeeze.',
    chapter: 'confirmation',
  },
  strongerSetup: {
    title: 'For Stronger Setup',
    claim: 'What this setup still needs.',
    facts: ['The conditions the score is missing'],
    action: 'Each one that lands loads it further.',
    chapter: 'confirmation',
  },
  tradingImplication: {
    title: 'Trading Implication',
    claim: 'The setup’s read, in one line.',
    rows: [
      { label: '75 and up', line: 'Dealer hedging feeds the run' },
      { label: '50 to 74', line: 'Developing: watch flow and price' },
      { label: '30 to 49', line: 'Early: it needs confirmation' },
    ],
    action: 'Pair the read with flow at the wall.',
    chapter: 'confirmation',
  },
  alternateSetup: {
    title: 'Alternate Setup',
    claim: 'The other direction, scored.',
    facts: ['Shows when the scores sit within 20'],
    action: 'A close second keeps the turn in play.',
    chapter: 'confirmation',
  },

  // ---- Recent Flow ----
  recentFlow: {
    title: 'Recent Flow',
    claim: 'Who is paying up at the level.',
    facts: ['The latest prints for this ticker'],
    chips: { label: 'Every print', items: ['Side', 'Premium', 'Contract'] },
    action: 'Flow into a wall confirms the test.',
    chapter: 'confirmation',
  },
  weightedFlow: {
    title: 'Flow lean',
    claim: 'Which way the money leans.',
    facts: ['Bullish premium minus bearish', 'Sweeps and blocks count extra'],
    action: 'Flow leaning into a wall confirms it.',
    chapter: 'confirmation',
  },
  flowConfidence: {
    title: 'Confidence',
    claim: 'How loud the read is.',
    facts: ['Prints, premium, and the lean add up'],
    cells: [
      { label: 'High', line: 'Plenty of evidence' },
      { label: 'Med.', line: 'Some evidence' },
      { label: 'Low', line: 'Thin evidence' },
    ],
    action: 'A loud read at a wall is the setup.',
    chapter: 'confirmation',
  },

  // ---- Volatility skew ----
  skew: {
    title: 'Volatility Skew',
    claim: 'Where the market pays up for protection.',
    facts: ['IV across strikes and dates', 'The wings measured against the center'],
    action: 'Know what you pay before you pick.',
    chapter: 'trade',
  },
  atmIV: {
    title: 'ATM IV',
    claim: 'The center of the smile.',
    facts: ['IV at the strike nearest spot', 'Call and put averaged'],
    action: 'Above ATM IV is rich; below is cheap.',
    chapter: 'trade',
  },
  skew25: {
    title: '25Δ Skew',
    claim: 'How much more the puts cost than the calls.',
    facts: ['25Δ put IV minus 25Δ call IV, in vol points'],
    cells: [
      { label: 'Positive', line: 'Pays for downside', tone: 'negative' },
      { label: 'Negative', line: 'Pays for upside', tone: 'positive' },
    ],
    action: 'Rich puts: sell them or buy the calls.',
    chapter: 'trade',
  },
  butterfly: {
    title: '25Δ Butterfly',
    claim: 'How much the wings cost over the middle.',
    facts: ['Both 25Δ wings averaged, minus ATM IV', 'Higher: the market pays for a big move'],
    action: 'Rich wings: spreads beat outright options.',
    chapter: 'trade',
  },
  skew10: {
    title: '10Δ Skew',
    claim: 'The tilt at the far wings.',
    facts: ['10Δ put IV minus 10Δ call IV', 'The price of tail risk'],
    action: 'Steeper than 25Δ: the crash hedge is bid.',
    chapter: 'trade',
  },
  skewSlope: {
    title: 'Skew Slope',
    claim: 'Which way the smile leans near the money.',
    facts: ['IV change per 1% of strike near spot'],
    cells: [
      { label: 'Negative', line: 'Higher strikes cheaper' },
      { label: 'Positive', line: 'Higher strikes richer' },
    ],
    action: 'A steep slope: the strike sets the price.',
    chapter: 'trade',
  },
  termSlope: {
    title: 'Term Slope',
    claim: 'Which expiration is the better price.',
    facts: ['Next date’s ATM IV minus this one’s'],
    cells: [
      { label: 'Negative', line: 'This date is rich' },
      { label: 'Positive', line: 'The next date is rich' },
    ],
    action: 'Buy the cheaper date when the trade allows.',
    chapter: 'trade',
  },
  smile: {
    title: 'Volatility Smile',
    claim: 'Strike by strike.',
    facts: ['IV by strike with ATM marked', 'Shaded above ATM: rich. Below: cheap'],
    action: 'Find your strike on the curve.',
    chapter: 'trade',
  },
  term: {
    title: 'Term Structure',
    claim: 'Date by date.',
    facts: ['ATM IV and the wings for each date'],
    action: 'Pick the date before the strike.',
    chapter: 'trade',
  },
  skewTable: {
    title: 'Skew table',
    claim: 'Every contract.',
    facts: ['Strike, IV, delta, and open interest', 'vs ATM: vol points over or under center'],
    action: 'Check vs ATM before you pay.',
    chapter: 'trade',
  },
  skewViews: {
    title: 'Smile, Term, and Table',
    claim: 'Three ways to price the chain.',
    action: 'Term picks the date; Smile picks the strike.',
    chapter: 'trade',
  },
  skewRange: {
    title: 'Strike range',
    claim: 'How much of the smile to show.',
    rows: [
      { label: 'Auto', line: 'Fits the expected move' },
      { label: 'Percent', line: 'Strikes within that distance' },
      { label: 'All', line: 'Every strike' },
    ],
    action: 'Auto for the trade, All for the tails.',
    chapter: 'trade',
  },

  // ---- Open interest ----
  oi: {
    title: 'Open Interest Analysis',
    claim: 'The positions behind the gamma.',
    facts: ['Every open contract by strike and date'],
    action: 'Big open interest at a level defends it.',
    chapter: 'trade',
  },
  oiStrike: {
    title: 'OI Strike Profile',
    claim: 'Where the contracts pile up.',
    facts: ['Open interest by strike', 'Net: calls minus puts. Call/Put: each side'],
    action: 'Match the piles to the gamma.',
    chapter: 'trade',
  },
  oiVolume: {
    title: 'OI Volume Profile',
    claim: 'Where the open contracts sit, strike by strike.',
    rows: [
      { label: 'Net OI', line: 'Calls minus puts at each strike' },
      { label: 'Call/Put', line: 'Calls up, puts down' },
    ],
    action: 'The longest Call/Put bars near price are crowded.',
    chapter: 'trade',
  },
  oiExpiry: {
    title: 'OI Expiry Breakdown',
    claim: 'Where the open contracts sit, date by date.',
    rows: [
      { label: 'Net OI', line: 'Calls minus puts on each date' },
      { label: 'Call/Put', line: 'Calls up, puts down' },
    ],
    action: 'The tallest Call/Put bars are the crowded dates.',
    chapter: 'trade',
  },
  oiLadder: {
    title: 'OI Strike Ladder',
    claim: 'Exact counts, strike by strike.',
    facts: ['Calls, puts, and total, sortable'],
    action: 'Sort by total: the top rows are crowded.',
    chapter: 'trade',
  },
  oiCumulative: {
    title: 'Cumulative OI',
    claim: 'The running total of positions.',
    facts: ['Open interest added up from the lowest strike', 'A steep step is a crowded strike'],
    action: 'The steeper curve is the crowded side.',
    chapter: 'trade',
  },

  // ---- Trade fields ----
  strike: {
    title: 'Strike',
    claim: 'Where the contract lives on the map.',
    facts: ['The exercise price'],
    action: 'Near a wall: level trade. Past it: breakout.',
    chapter: 'trade',
  },
  contractOI: {
    title: 'Contract open interest',
    claim: 'What is already open in this contract.',
    facts: ['Open contracts at this strike, date, and side'],
    action: 'Compare size with the last reported OI.',
    chapter: 'trade',
  },
  dte: {
    title: 'Days to expiry',
    claim: 'Time left on the contract.',
    facts: ['Calendar days; 0DTE expires today'],
    action: 'Make the contract outlive the trade.',
    chapter: 'expirations',
  },

  expiration: {
    title: 'Expiration',
    claim: 'The day the contract stops trading.',
    facts: ['0DTE expires today'],
    action: 'Make the contract outlive the trade.',
    chapter: 'expirations',
  },

  printSpot: {
    title: 'Spot',
    claim: 'Where the stock traded at the print.',
    facts: ['The underlying price when the order filled'],
    action: 'Price now against spot then: the move since.',
    chapter: 'confirmation',
  },
  iv: {
    title: 'Implied volatility',
    claim: 'What the contract is priced for.',
    facts: ['The annual move the price implies', '20% to 23% is 3 vol points'],
    action: 'Buy when your expected move is bigger.',
    chapter: 'trade',
  },
  deltaOption: {
    title: 'Option delta',
    claim: 'How much the contract moves per dollar.',
    facts: ['Its move for a $1 move in the stock', 'Calls positive, puts negative'],
    action: 'Match delta to the move you expect.',
    chapter: 'trade',
  },
  vsATM: {
    title: 'IV vs ATM',
    claim: 'Rich or cheap against the center.',
    facts: ['This strike’s IV minus ATM IV'],
    cells: [
      { label: 'Plus', line: 'Paying up' },
      { label: 'Minus', line: 'A discount' },
    ],
    action: 'Check it before you pay.',
    chapter: 'trade',
  },
  premium: {
    title: 'Premium',
    claim: 'The dollars on the print.',
    facts: ['Price × contracts × 100'],
    action: 'The biggest premium at a wall matters most.',
    chapter: 'confirmation',
  },
  size: {
    title: 'Trade size',
    claim: 'How many contracts hit the tape.',
    facts: ['Size it against the open interest'],
    action: 'Compare size with the last reported OI.',
    chapter: 'confirmation',
  },
  execution: {
    title: 'Execution side',
    claim: 'Who was in a hurry.',
    rows: [
      { label: 'AA, A', line: 'Paid the ask or more' },
      { label: 'B, BB', line: 'Sold at the bid or less' },
      { label: 'M', line: 'Met in the middle' },
    ],
    action: [
      { icon: 'up', text: 'Calls bought, puts sold: bullish' },
      { icon: 'down', text: 'Puts bought, calls sold: bearish' },
    ],
    chapter: 'confirmation',
  },
  orderType: {
    title: 'Order type',
    claim: 'How the order was filled.',
    rows: [
      { label: 'Sweep', line: 'Many venues at once: urgency' },
      { label: 'Block', line: 'One big fill: conviction' },
      { label: 'Split', line: 'One order in several fills' },
    ],
    action: 'At a wall, any of them confirms it.',
    chapter: 'confirmation',
  },
  otm: {
    title: 'Out of the money',
    claim: 'How far the strike sits from price.',
    rows: [
      { label: 'Plus', line: 'Out of the money: price has to move' },
      { label: 'Minus', line: 'Already in the money' },
    ],
    action: 'Far and short-dated bets on a fast move.',
    chapter: 'trade',
  },
  heatScore: {
    title: 'Heat Score',
    claim: 'The prints that matter most.',
    chips: {
      label: 'Scores',
      items: ['Premium', 'Size', 'Sweep', 'Expiry', 'Distance', 'Volume and OI'],
    },
    action: 'The hottest print at a wall comes first.',
    chapter: 'confirmation',
  },
  strategy: {
    title: 'Options Profit Calculator',
    claim: 'From level to payoff in one tap.',
    facts: ['Opens with this ticker and strategy loaded'],
    action: 'Set strikes to the levels, see the payoff.',
    chapter: 'trade',
  },
} satisfies Record<string, GEXLesson>;

export type GEXLessonId = keyof typeof gexLessons;

/** Guide access to the lessons whose dashboard triggers stay hidden on touch screens. */
export const gexGuideControls: { id: GEXLessonId; choices?: GEXFact[] }[] = [
  {
    id: 'displayModes',
    choices: [
      { label: 'Net GEX', line: gexLessons.netMode.claim },
      { label: 'Call/Put', line: gexLessons.splitMode.claim },
    ],
  },
  {
    id: 'viewModes',
    choices: [
      { label: 'Graph', line: gexLessons.graphMode.claim },
      { label: 'Table', line: gexLessons.tableMode.claim },
    ],
  },
  {
    id: 'strikeRange',
    choices: [
      { label: 'Near', line: gexLessons.near.claim },
      { label: 'All', line: gexLessons.allStrikes.claim },
    ],
  },
  {
    id: 'expirations',
    choices: [
      { label: 'Today', line: 'Today’s contracts' },
      { label: '1W / 1M', line: 'The next 7 / 30 days' },
      { label: '3M / 6M', line: 'The next 90 / 180 days' },
      { label: 'All', line: 'Every expiration in the chain' },
      { label: 'Custom dates', line: 'Pick the exact dates' },
    ],
  },
  {
    id: 'skewViews',
    choices: [
      { label: 'Smile', line: gexLessons.smile.claim },
      { label: 'Term', line: gexLessons.term.claim },
      { label: 'Table', line: gexLessons.skewTable.claim },
    ],
  },
  { id: 'skewRange' },
  { id: 'putBars' },
  { id: 'callBars' },
];

/** The guide's five chapters: a claim and three steps each, and the live section they open. */
export const gexChapters: {
  id: GEXChapter;
  title: string;
  claim: string;
  steps: [string, string, string];
  action: string;
  section: string;
  fields: GEXLessonId[];
}[] = [
  {
    id: 'regime',
    title: 'Read the regime',
    claim: 'Know in one glance whether the tape fades moves or feeds them.',
    steps: [
      'Net GEX positive: dealers lean against the move and ranges hold.',
      'Net GEX negative: dealers chase the move and breaks run.',
      'Price and flow at the level pick the side.',
    ],
    action: 'Show Net GEX',
    section: 'metrics',
    fields: ['net', 'total'],
  },
  {
    id: 'levels',
    title: 'Mark the levels',
    claim: 'The chain tells you where price fights and where it runs.',
    steps: [
      'Call Wall above, Put Wall below, Zero Gamma where the regime flips.',
      'Price reacts at a wall three ways: rejection, reclaim, or break.',
      'The reaction is the trade.',
    ],
    action: 'Show key levels',
    section: 'strike',
    fields: ['callWall', 'putWall', 'zero'],
  },
  {
    id: 'expirations',
    title: 'Pick the expirations',
    claim: 'The dates behind a level decide how long it holds.',
    steps: [
      '0DTE, Weekly, and Monthly Exp show how much gamma expires soon.',
      'The heatmap shows which dates sit behind each strike.',
      'Select the window that matches your hold. The whole map rebuilds for it.',
    ],
    action: 'Show the heatmap',
    section: 'heatmap',
    fields: ['concentration', 'heatmap'],
  },
  {
    id: 'confirmation',
    title: 'Confirm the move',
    claim: 'Positioning says where. Flow says when.',
    steps: [
      'Signals name the levels in play: Volatility, Magnet, Support, Resistance.',
      'Recent Flow shows who is paying up at that level.',
      'The Squeeze Screener scores both directions and names the stronger one.',
    ],
    action: 'Show signals and flow',
    section: 'signals',
    fields: ['delta', 'squeeze', 'weightedFlow'],
  },
  {
    id: 'trade',
    title: 'Build the trade',
    claim: 'Trigger, invalidation, target, payoff. All on this page.',
    steps: [
      'Trigger: the reaction at the level.',
      'Invalidation: the level behind it. Target: the next concentration.',
      'Stress-test IV, then open the Options Profit Calculator with the strategy loaded.',
    ],
    action: 'Return to the gamma map',
    section: 'metrics',
    fields: ['skew', 'simulation', 'strategy'],
  },
];

export const GEX_SPEED = {
  title: 'The fastest gamma map in the market.',
  body: 'Every tick re-prices the exposure, re-draws the walls, and re-measures the distance to every level, second by second. No ten‑minute snapshots.',
};

export const GEX_DEEPER_READING = [
  {
    title: 'How to Read Gamma Exposure (GEX) Like a Pro',
    href: 'https://www.insiderfinance.io/resources/how-to-read-gamma-exposure-gex-like-a-pro',
  },
  {
    title: 'The Ultimate Guide to Gamma Exposure (GEX)',
    href: 'https://www.insiderfinance.io/resources/the-ultimate-guide-to-gamma-exposure-gex',
  },
] as const;
